import {
    handleCartModifications,
    mapCartItem,
    useCurrentCartInfo,
    VirtualSetCartModificationsType,
} from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { AddMultipleProductsByUuidInputApi, useAddVirtualSetProductsByUuidToCartMutationApi } from 'graphql/generated';
import { useChangePaymentMutation } from 'hooks/cart/useChangePaymentInCart';
import { useLatest } from 'hooks/ui/useLatest';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmChangeCartItemEventType } from 'types/gtm';
import { onChangeCartItemsGtmEventHandler } from 'utils/Gtm/EventHandlers';
import { useGtmCartEventInfo } from 'utils/Gtm/Gtm';
import { mapGtmCartItemType } from 'utils/Gtm/Mappers';

export const useAddVirtualSetProductsToCart = () => {
    const [mutationState, addItemsByUuid] = useAddVirtualSetProductsByUuidToCartMutationApi();
    const { selectedStoreUuid, defaultStoreUuid, updateCartUuid } = usePersistStore((s) => s);
    const [changePaymentInCart] = useChangePaymentMutation();
    const { t } = useTranslation();
    const { refetchCart: refetchCartInfo } = useCurrentCartInfo(false);
    const { currencyCode, url } = useDomainConfig();
    const gtmCartEventInfo = useGtmCartEventInfo();
    const gtmCart = useLatest(gtmCartEventInfo.cart);

    const addVirtualSetProductsToCart = useCallback(
        async (
            products: AddMultipleProductsByUuidInputApi[],
        ): Promise<VirtualSetCartModificationsType | null | undefined> => {
            let cartUuid = null;
            const result = await addItemsByUuid({
                input: {
                    cartUuid,
                    products,
                    shouldMerge: true,
                },
                defaultStoreUuid,
                selectedStoreUuid,
            });

            const data = result.data?.AddMultipleItemsByUuidToCart;
            const modifications = data?.modifications;

            if (modifications) {
                cartUuid = result.data?.AddMultipleItemsByUuidToCart.uuid;
                updateCartUuid(cartUuid ?? null);
                handleCartModifications(modifications, t, changePaymentInCart);

                const addedProducts = modifications.multipleAddedProductModifications.addedProducts;
                if (addedProducts.length > 0) {
                    const { gtmProducts, eventValue, eventValueWithTax } = addedProducts.reduce<{
                        gtmProducts: GtmChangeCartItemEventType['products'];
                        eventValue: number;
                        eventValueWithTax: number;
                    }>(
                        (acc, addedProduct, index) => {
                            if (addedProduct.cartItem === null) {
                                return acc;
                            }

                            const mappedCartItem = mapCartItem(addedProduct.cartItem, currencyCode);
                            acc.gtmProducts.push(
                                mapGtmCartItemType(mappedCartItem, index, url, addedProduct.addedQuantity),
                            );
                            acc.eventValue +=
                                Number.parseFloat(addedProduct.cartItem.product.price.priceWithoutVat) *
                                Math.abs(addedProduct.addedQuantity);
                            acc.eventValueWithTax +=
                                Number.parseFloat(addedProduct.cartItem.product.price.priceWithVat) *
                                Math.abs(addedProduct.addedQuantity);

                            return acc;
                        },
                        {
                            gtmProducts: [],
                            eventValue: 0,
                            eventValueWithTax: 0,
                        },
                    );

                    if (gtmProducts.length > 0) {
                        setTimeout(() => {
                            onChangeCartItemsGtmEventHandler(
                                gtmCart.current,
                                gtmProducts,
                                currencyCode,
                                eventValue,
                                eventValueWithTax,
                                'virtual set',
                            );
                        }, 0);
                    }

                    dispatchBroadcastChannel({ name: 'refetchCart' });
                    refetchCartInfo();
                }
            }

            return modifications as VirtualSetCartModificationsType | null | undefined;
        },
        [
            addItemsByUuid,
            changePaymentInCart,
            currencyCode,
            defaultStoreUuid,
            gtmCart,
            refetchCartInfo,
            selectedStoreUuid,
            t,
            updateCartUuid,
            url,
        ],
    );

    return {
        addVirtualSetProductsToCart,
        addVirtualSetProductsMutationState: mutationState,
    };
};
